import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Text,
  Tooltip,
  Box,
  Skeleton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Divider,
  Toast,
  VisuallyHidden,
  MenuItem as ChakraMenuItem
} from '@chakra-ui/react';
import {
  faBarsFilter,
  faDeleteRight,
  faFloppyDisk,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonText,
  ClearButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  SearchInput
} from '@morressier/alembic';
import { useLocation, useNavigate } from 'react-router-dom';
import { IMySubmissionFilter } from 'src/api/queries';
import ListLoader from 'src/components/ListLoader';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { formatDate, formatDeadlineDate } from 'src/utils/date';
import { useForm } from 'react-hook-form';
import {
  parseSubmissionQueryParams,
  updateQueryParams
} from 'src/utils/submissionsList';
import SubmissionLoader from '../Organization/Workspace/Submissions/SubmissionLoader';
import { differenceInCalendarDays } from 'date-fns';
import { Helmet } from 'react-helmet';
import {
  useSubmissionDashBoardConfig,
  useSubmissions,
  defaultValues,
  useSavedFilters
} from './hooks';
import { ISubmissionListItem } from '@publishing-engine/domain/views';
import { STATUS_MAP } from '.';
import { ISubmissionStatus } from 'src/constants';
import { getPhaseContent } from 'src/utils/phaseCopy';
import FilterDrawer from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SortMenu, {
  SortField
} from '../Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import { SubmissionAuthors } from '../Organization/Workspace/Submissions/SubmissionAuthors';
import {
  ISavedFilterSets,
  ISubmissionWorkflowState,
  IWorkPhase,
  NON_SUBMITTED_WORKFLOW_PHASES,
  SUBMITTED_WORKFLOW_PHASES
} from '@publishing-engine/domain/models';
import SaveFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/SaveFilterModal';
import ManageSavedFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/ManageSavedFiltersModal';
import SaveFilterText from '../Organization/Workspace/Submissions/SaveFilterText';
import SubmissionActionMenu from '../Organization/Workspace/Submissions/SubmissionActionMenu';
import ContactJournal from 'src/components/ContactPublisher/ContactPublisher';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

export type filtersItem =
  | 'workflowPhase'
  | 'journalId'
  | 'documentType'
  | 'assigneeId'
  | 'tagId'
  | 'focusIssueId';

type RowDetails = {
  submissionId: string;
  submissionContextId: string;
  manuscriptId: string;
};

const SubmittedSubmissions = () => {
  const { watch, reset, resetField, setValue, getValues } =
    useForm<IMySubmissionFilter>({
      defaultValues: {
        ...defaultValues,
        submissionType: 'my-submissions-active'
      }
    });
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<SortField | null>(null);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [showSaveFiltersModal, setShowSaveFiltersModal] =
    useState<boolean>(false);
  const [showManageFiltersModal, setShowManageFiltersModal] =
    useState<boolean>(false);
  const filters = watch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const { data: dashBoardConfig } = useSubmissionDashBoardConfig(
    'my-submissions-active'
  );

  const { data: savedFilters, isLoading: isSavedFiltersLoading } =
    useSavedFilters('my-submissions-active');

  const offset = (page - 1) * pageSize;
  const submittedFilters = useMemo(() => {
    const selectedPhases = filters.workflowPhase.filter(
      phase => !NON_SUBMITTED_WORKFLOW_PHASES.includes(phase)
    );
    return {
      ...filters,
      workflowPhase: selectedPhases.length
        ? selectedPhases
        : SUBMITTED_WORKFLOW_PHASES
    };
  }, [filters.journalId, filters.workflowPhase, filters.searchParams]);
  const { data, isLoading, isFetchingNextPage } =
    useSubmissions<ISubmissionListItem>(
      'active',
      page,
      offset,
      submittedFilters,
      sort ?? undefined,
      pageSize
    );

  const clearFilters = () => {
    reset({ ...defaultValues, submissionType: 'my-submissions-active' });
    setSearchValue('');
    setSelectedFilters({});
    // Clear URL parameters
    navigate({
      pathname: location.pathname,
      search: '?submissionType=my-submissions-active'
    });
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        workflowPhase: filters.workflowPhase ?? [],
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.journalId, filters.workflowPhase, filters.searchParams]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Parse query parameters on initial load
  useEffect(() => {
    const { filters: newFilters, sort: newSort } = parseSubmissionQueryParams(
      location.search
    );
    if (newFilters.submissionType === 'my-submissions-active') {
      setSearchValue(newFilters.searchParams);
      reset(newFilters);
      setSelectedFilters({
        journalId: newFilters.journalId ?? [],
        workflowPhase: newFilters.workflowPhase ?? []
      });
      setSort(newSort);
    }
  }, [reset]);

  // Sync URL query params to formState
  useEffect(() => {
    updateQueryParams(
      {
        ...filters,
        sort: sort ? `${sort.label},${sort.value},${sort.direction}` : ''
      },
      location,
      navigate
    );
  }, [filters, sort, navigate]);
  const total = data?.pages?.[data.pages.length - 1]?.total ?? 0;
  const hasFilters =
    !!filters.workflowPhase.length ||
    !!filters.journalId.length ||
    !!filters.searchParams;

  function renderDeadlineCell(deadline?: string | Date) {
    if (!deadline) {
      return <Text>N/A</Text>;
    }

    const parsedDate = new Date(deadline);
    const daysLeft = differenceInCalendarDays(parsedDate, new Date());
    const isUrgent = daysLeft <= 5;

    return (
      <Text color={isUrgent ? 'actionSecondaryCautionContent' : ''}>
        {formatDeadlineDate(parsedDate)}
      </Text>
    );
  }

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as keyof IMySubmissionFilter, values as any);
    });
  };

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, total);

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const openItem = (id: string) => {
    navigate(`/app/my-submissions/${id}?${searchParams.toString()}`);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [rowDetails, setRowDetails] = useState<RowDetails | null>(null);
  const openContactJournalModal = (id: string) => {
    const submission = data?.pages[0]?.items.find(m => m.id === id);
    setIsOpen(true);
    setRowDetails({
      submissionId: id,
      submissionContextId: submission?.journal?.id ?? '',
      manuscriptId: submission?.manuscriptId ?? ''
    });
  };
  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: keyof IMySubmissionFilter
  ) => {
    const currentFilter = getValues(fieldName) as string[];
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    );
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setFilterName(null);
  };

  const handleSetValues = (data: ISavedFilterSets, name: string) => {
    setValue('searchParams', data?.searchParams ?? '');
    setValue(
      'workflowPhase',
      (data?.workflowPhase as ISubmissionWorkflowState[]) ?? []
    );
    setValue('journalId', data?.journalId ?? []);
    setFilterName(name);
    setSearchValue(data?.searchParams ?? '');
    setSelectedFilters({
      workflowPhase:
        (data?.workflowPhase as ISubmissionWorkflowState[])?.map(w => w) ?? [],
      journalId: data?.journalId ?? []
    });
    return Toast({
      variant: 'success',
      title: `${name} Filter Applied`
    });
  };

  const onDeleteClearFilters = () => {
    clearFilters();
    setFilterName(null);
  };

  const handleSaveFiltersModal = (name: string | null) => {
    setShowSaveFiltersModal(prev => !prev);
    if (name) setFilterName(name);
  };
  return (
    <>
      <Flex flexDirection={'column'} gap={4} width="100%">
        {showSaveFiltersModal ? (
          <SaveFiltersModal
            isOpen={showSaveFiltersModal}
            onClose={handleSaveFiltersModal}
            filter={filters}
            view="my-submissions-active"
          />
        ) : null}
        {showManageFiltersModal ? (
          <ManageSavedFiltersModal
            isOpen={showManageFiltersModal}
            onClose={() => setShowManageFiltersModal(false)}
            savedFilters={savedFilters?.response}
            availableFilters={dashBoardConfig?.filters}
            isSavedFiltersLoading={isSavedFiltersLoading}
            clearFilters={onDeleteClearFilters}
            view="my-submissions-active"
          />
        ) : null}
        <Helmet>
          <title>Submitted Submissions</title>
        </Helmet>
        <Flex gap={2} flexDirection={'column'}>
          <Flex
            gap={3}
            mb={2}
            width="100%"
            alignItems={{ base: 'stretch', md: 'center' }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box width="100%">
              <Flex gap={2} alignItems="center">
                <SearchInput
                  placeholder="Search submissions by title or manuscript ID"
                  iconLeft={<FontAwesomeIcon icon={faSearch} />}
                  width="100%"
                  aria-label="Search submissions"
                  name="searchParams"
                  value={searchValue}
                  enterKeyHint="search"
                  onChange={e => setSearchValue(e.currentTarget.value)}
                  onInputClear={() => {
                    setSearchValue('');
                    resetField('searchParams');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }
                  }}
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }
                  }}
                />
                <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                  <Button
                    variant="primary"
                    aria-label="Search"
                    onClick={() => {
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </Box>
              </Flex>
            </Box>
            {dashBoardConfig?.filters && (
              <Box width={{ base: '100%', md: 'auto' }}>
                <FilterDrawer
                  filterGroups={dashBoardConfig.filters.map((filter: any) => ({
                    ...filter,
                    items: filter.items ?? []
                  }))}
                  watch={watch}
                  onSubmit={handleFiltersSubmit}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              </Box>
            )}
            <Flex width={{ base: '100%', md: 'auto' }}>
              <Menu ariaLabel="filters">
                {({ onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      variant="secondary"
                      iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                      aria-label="Saved Filters"
                      width={{ base: '100%', md: 'auto' }}
                    >
                      Saved Filters
                    </MenuButton>
                    <MenuList
                      textStyle="headingItem"
                      minWidth={{ base: 'calc(100vw - 2rem)', md: 'auto' }}
                      maxWidth={{ base: 'calc(100vw - 2rem)', md: '420px' }}
                    >
                      <ChakraMenuItem style={{ display: 'none' }} />
                      {savedFilters?.response?.length ? (
                        <>
                          <Box maxH="300px" overflowY="auto" maxW="350px">
                            {savedFilters.response.map(item => (
                              <>
                                <MenuItem
                                  onClick={() => {
                                    const filter =
                                      item.filters as ISavedFilterSets;
                                    handleSetValues(filter, item.name);
                                  }}
                                  ariaLabel={item.name}
                                  key={item.id}
                                >
                                  <Text textStyle="headingLine" isTruncated>
                                    {item.name}
                                  </Text>
                                  {
                                    <SaveFilterText
                                      saveFilters={
                                        item.filters as ISavedFilterSets
                                      }
                                      availableFilters={
                                        dashBoardConfig?.filters
                                      }
                                      manageTags={[]}
                                    />
                                  }
                                </MenuItem>
                                <MenuDivider />
                              </>
                            ))}
                          </Box>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            padding={3}
                            position="sticky"
                          >
                            <ButtonText
                              onClick={() => {
                                setShowManageFiltersModal(true);
                                onClose();
                              }}
                            >
                              Manage Filters
                            </ButtonText>
                          </Flex>
                        </>
                      ) : (
                        <Flex
                          flexDirection={'column'}
                          gap={4}
                          padding={{ base: 4, md: 8 }}
                          justifyContent={'center'}
                          alignItems={'center'}
                          maxW="350px"
                        >
                          <Text textStyle="headingGroup">No Saved Filters</Text>
                          <Text textStyle="textBody" textAlign={'center'}>
                            You do not have any saved filters yet. Try applying
                            some filters and then click 'Save Filters' to save
                            them for later usage.
                          </Text>
                        </Flex>
                      )}
                    </MenuList>
                  </>
                )}
              </Menu>
            </Flex>
          </Flex>
        </Flex>
        <Skeleton isLoaded={!isLoading}>
          <Flex
            alignItems={{ base: 'flex-start', lg: 'center' }}
            gap={3}
            height={{ lg: 5 }}
            flexDirection={{ base: 'column', lg: 'row' }}
            justifyContent={'space-between'}
            width="100%"
          >
            <Flex gap={2} flexWrap="wrap" alignItems="center">
              <Text
                textStyle="textBody"
                whiteSpace={{ base: 'normal', md: 'nowrap' }}
              >
                {total > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> –{' '}
                    <strong>{endIndex}</strong> of <strong>{total}</strong>{' '}
                    submission{total === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> submission
                  </>
                )}
              </Text>
              {hasFilters && (
                <>
                  {filterName ? (
                    <Box marginLeft={2}>
                      <Text textStyle="textBody">
                        <strong>{filterName}</strong> applied
                      </Text>
                    </Box>
                  ) : (
                    <ButtonText
                      leftIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                      variant="secondary"
                      onClick={() => setShowSaveFiltersModal(true)}
                      marginX={{ base: 0, md: 2 }}
                    >
                      Save Filters
                    </ButtonText>
                  )}
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                    variant="secondary"
                    onClick={() => {
                      clearFilters();
                      setFilterName(null);
                    }}
                  >
                    Clear Filters
                  </ButtonText>
                </>
              )}
            </Flex>
            <Divider
              orientation="horizontal"
              flex="1"
              mx={4}
              borderColor="gray.300"
              display={{ base: 'none', lg: 'block' }}
            />
            {dashBoardConfig?.sortOptions && (
              <Box width={{ base: '100%', lg: 'auto' }}>
                <SortMenu
                  appliedSort={sort}
                  options={dashBoardConfig.sortOptions}
                  onApply={handleSortApply}
                  onReset={handleSortReset}
                />
              </Box>
            )}
          </Flex>
          {!!hasFilters && (
            <Flex alignItems="center" gap={2} wrap="wrap" marginTop={2}>
              {dashBoardConfig?.filters?.map(filterConfig => {
                // Type guard to check if filterConfig has 'items'
                const hasItems = (fc: any): fc is { items: any[] } =>
                  Array.isArray(fc.items);

                if (
                  !hasItems(filterConfig) ||
                  filterConfig.items.length === 0
                ) {
                  return null;
                }

                const filterKey = filterConfig.key as keyof typeof filters;
                const filterValues = filters?.[filterKey];

                if (!Array.isArray(filterValues) || filterValues.length === 0) {
                  return null;
                }

                return filterValues.map(value => {
                  const item = filterConfig.items.find(
                    (i: any) => i.value === value
                  );
                  const label = item?.name ?? value;

                  return (
                    <Badge
                      key={`${filterKey}-${value}`}
                      size="base"
                      variant="interactive"
                    >
                      <Flex alignItems="center" gap={2}>
                        {label}
                        <FontAwesomeIcon
                          icon={faXmark}
                          cursor="pointer"
                          onClick={() =>
                            removeSingleFilter(
                              value,
                              filterKey as keyof IMySubmissionFilter
                            )
                          }
                        />
                      </Flex>
                    </Badge>
                  );
                });
              })}
            </Flex>
          )}
        </Skeleton>
        <Box width="100%">
          {isLoading && <ListLoader />}
          {data?.pages[0] && total !== 0 ? (
            <>
              <Box overflowX="auto">
                <Box minWidth={{ base: '860px', md: '100%' }}>
                  <Flex flexDirection={'column'}>
                    {/* @ts-ignore */}
                    <Table size="sm" width="100%">
                      <Thead>
                        <Tr
                          data-testId="submissions-table-header"
                          gap={4}
                          sx={{
                            '& th': {
                              textTransform: 'none',
                              color: 'black',
                              background: 'white'
                            }
                          }}
                        >
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Submission Title & Authors
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Journal
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem">Submission Date</Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Deadline
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Phase
                            </Text>
                          </Th>
                          <Th>
                            <VisuallyHidden>Actions</VisuallyHidden>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.pages.map(({ items }, i) => (
                          <React.Fragment key={i}>
                            {items.map(
                              ({
                                id,
                                submission: { title, authors },
                                workflow: {
                                  currentState,
                                  currentDeadline,
                                  submissionCreated
                                },
                                journal,
                                manuscriptId
                              }) => {
                                const status =
                                  STATUS_MAP[currentState as ISubmissionStatus];
                                const phaseHeading = getPhaseContent(
                                  currentState as IWorkPhase,
                                  'author'
                                ).heading;
                                return (
                                  <Tr key={id} gap={4} background="white">
                                    <Td
                                      onClick={() => openItem(id)}
                                      cursor="pointer"
                                      _hover={{ bg: 'gray.50' }}
                                    >
                                      <ClearButton
                                        width="100%"
                                        ariaLabel={`Open submission ${title}`}
                                        data-testId="submissions-row-title"
                                      >
                                        <Flex
                                          direction="column"
                                          alignItems="flex-start"
                                          justifyContent="flex-start"
                                          width="100%"
                                          minWidth={0}
                                          textAlign="left"
                                        >
                                          <Tooltip
                                            hasArrow
                                            borderRadius={4}
                                            placement="top"
                                            label={'View Submission'}
                                          >
                                            <Box width="100%">
                                              <Text
                                                textStyle="headingLine"
                                                whiteSpace="normal"
                                              >
                                                {manuscriptId}
                                              </Text>
                                            </Box>
                                            <Box
                                              isTruncated
                                              noOfLines={1}
                                              width="100%"
                                            >
                                              <Text
                                                textStyle="textBody"
                                                whiteSpace="normal"
                                                textDecoration="none"
                                                _hover={{
                                                  textDecoration: 'underline',
                                                  textDecorationColor:
                                                    'actionPrimaryFill'
                                                }}
                                              >
                                                <RichTextPreview
                                                  value={title}
                                                  color="actionPrimaryFill"
                                                />
                                              </Text>
                                            </Box>
                                          </Tooltip>

                                          <SubmissionAuthors
                                            authors={authors}
                                          />
                                        </Flex>
                                      </ClearButton>
                                    </Td>
                                    <Td
                                      textStyle="textBody"
                                      textTransform="uppercase"
                                    >
                                      {journal?.shortname}
                                    </Td>

                                    <Td>
                                      {submissionCreated !== null
                                        ? formatDate(
                                            new Date(submissionCreated ?? 'N/A')
                                          )
                                        : 'N/A'}
                                    </Td>
                                    <Td>
                                      {currentDeadline
                                        ? renderDeadlineCell(currentDeadline)
                                        : 'N/A'}
                                    </Td>
                                    <Td>
                                      <Flex
                                        gap={1}
                                        overflow="hidden"
                                        wrap="wrap"
                                      >
                                        <Badge
                                          size="context"
                                          variant={status?.variant}
                                        >
                                          {status?.icon && (
                                            <FontAwesomeIcon
                                              icon={status.icon}
                                            />
                                          )}{' '}
                                          {phaseHeading ||
                                            status?.label ||
                                            currentState}
                                        </Badge>
                                      </Flex>
                                    </Td>
                                    <Td textStyle="textBody">
                                      <Flex justifyContent={'flex-end'}>
                                        <SubmissionActionMenu
                                          submissionId={id}
                                          handleClick={() => openItem(id)}
                                          handleContactClick={() =>
                                            openContactJournalModal(id)
                                          }
                                        />
                                      </Flex>
                                    </Td>
                                  </Tr>
                                );
                              }
                            )}
                          </React.Fragment>
                        ))}
                        {isFetchingNextPage && <ListLoader />}
                      </Tbody>
                    </Table>
                  </Flex>
                </Box>
              </Box>
              <TablePagination
                ariaLabel="Submission pagination"
                currentPage={page}
                totalItems={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          ) : (
            !isLoading && (
              <SubmissionLoader
                title="You do not have any submissions"
                subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
                bgColor="neutralSecondaryFill"
              />
            )
          )}
        </Box>
        {rowDetails && (
          <Modal
            width="min(720px, calc(100vw - 2rem))"
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <ContactJournal
              submissionId={rowDetails?.submissionId}
              submissionContextId={rowDetails?.submissionContextId}
              manuscriptId={rowDetails?.manuscriptId}
              setIsContactModalOpen={setIsOpen}
            />
          </Modal>
        )}
      </Flex>
    </>
  );
};

export default SubmittedSubmissions;
